import { formatRM } from '@bazaar/shared';

export interface LegendItem {
  key: string;
  label: string;
  qty: number;
  color: string;
  /** Shown as a "N%" chip when set (the pie chart has one; the bar chart doesn't). */
  pct?: number;
  revenueCents?: number;
}

export interface LegendProps {
  items: LegendItem[];
  /** Highlights the row matching the chart's current hover/focus, if any. */
  activeKey?: string | null;
  onActivate?: (key: string | null) => void;
}

/**
 * The one dependable, always-present identity channel for both charts (see
 * the data-viz skill: "never make the reader rely on color-matching alone").
 * A small colour swatch sits beside plain ink/muted text - never on top of
 * it - so every row stays legible regardless of the swatch's own colour.
 */
export function Legend({ items, activeKey, onActivate }: LegendProps) {
  if (items.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1" data-testid="chart-legend">
      {items.map((item) => {
        const active = activeKey === item.key;
        return (
          <li key={item.key}>
            <button
              type="button"
              onClick={() => onActivate?.(active ? null : item.key)}
              onMouseEnter={() => onActivate?.(item.key)}
              onMouseLeave={() => onActivate?.(null)}
              className="flex w-full items-center gap-2 min-h-11 rounded-lg px-2 py-1 text-left transition-colors hover:bg-bg focus-visible:bg-bg"
              style={active ? { backgroundColor: 'var(--bg)' } : undefined}
            >
              <span
                aria-hidden="true"
                className="shrink-0 h-3.5 w-3.5 rounded-sm"
                style={{ backgroundColor: item.color }}
              />
              <span className="min-w-0 flex-1 truncate font-body text-sm text-text">
                {item.label}
              </span>
              {item.pct !== undefined && (
                <span className="shrink-0 font-body text-sm text-muted">{item.pct}%</span>
              )}
              <span className="shrink-0 font-heading font-semibold text-sm text-ink">
                {item.qty}
              </span>
              {item.revenueCents !== undefined && (
                <span className="shrink-0 font-body text-xs text-muted w-16 text-right">
                  {formatRM(item.revenueCents)}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
